import React from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@lib/utils';
import { ChevronThinIcon } from '@/components/icons/chevron-thin-icon';
import { TrackedAnchor } from '@/components/ancher';

/**
 * 목록 페이지 공용 페이지네이션 UI — blog·news·faq·publications 가 같은 시안을 쓴다.
 *
 * `pages` 는 `getCenteredPagination`(`@/utils`) 산출을 그대로 받는다: 숫자 최대 5개짜리
 * 가운데 정렬 창 + 항상 포함되는 'prev'/'next'. 창이 현재 페이지를 항상 포함하므로
 * "창의 첫 숫자 = 현재" ⟺ 1페이지, "마지막 숫자 = 현재" ⟺ 마지막 페이지다 —
 * 이 판별로 경계의 화살표를 비활성화한다.
 *
 * 라우트별로 다른 건 링크 생성뿐이라 `hrefBase` 문자열로 받는다(`hrefBase + 페이지 번호`).
 * props 를 직렬화 가능한 값으로만 유지할 것 — faq 처럼 'use client' 경계 안에서도 렌더된다.
 */
export default function ListPagination({
  pages,
  current,
  hrefBase,
}: {
  pages: string[];
  current: string;
  hrefBase: string;
}) {
  const t = useTranslations('Pagination');
  const numbers = pages.filter(_page => _page !== 'prev' && _page !== 'next');

  const targetPage = (page: string) => {
    switch (page) {
      case 'prev':
        return Number(current) - 1;
      case 'next':
        return Number(current) + 1;
      default:
        return page;
    }
  };

  const label = (page: string) => {
    switch (page) {
      case 'prev':
        return (
          <ChevronThinIcon
            direction="left"
            className="size-6 text-3b-text-primary"
          />
        );
      case 'next':
        return (
          <ChevronThinIcon
            direction="right"
            className="size-6 text-3b-text-primary"
          />
        );
      default:
        return <span>{page}</span>;
    }
  };

  return (
    <ol className="flex w-full items-center justify-center">
      {pages.map(_page => {
        const isCurrent = _page === current;
        const disabled =
          (_page === 'prev' && current === numbers[0]) ||
          (_page === 'next' && current === numbers[numbers.length - 1]);
        // 숫자는 보이는 글자가 곧 접근 이름이라 화살표에만 aria-label 을 준다
        const ariaLabel =
          _page === 'prev'
            ? t('prev')
            : _page === 'next'
              ? t('next')
              : undefined;

        return (
          <li
            key={_page}
            className="flex h-10 w-10 items-center justify-center p-1"
          >
            {disabled ? (
              // 경계의 화살표는 이동할 곳이 없다 — 링크가 아니라 장식으로 남긴다
              <span
                aria-hidden="true"
                className="flex h-full w-full items-center justify-center opacity-30"
              >
                {label(_page)}
              </span>
            ) : (
              <TrackedAnchor
                href={`${hrefBase}${targetPage(_page)}`}
                label={`pagination_${_page}`}
                position="main_content"
                aria-current={isCurrent ? 'page' : undefined}
                aria-label={ariaLabel}
                className={cn(
                  'focus-ring-tight flex h-full w-full items-center justify-center rounded-lg text-[12px] font-semibold leading-[12px] tracking-[-0.04px] text-3b-text-secondary md:text-body1',
                  isCurrent
                    ? 'pointer-events-none bg-3b-blue-lightAlpha-50 text-3b-blue-light-900'
                    : 'transition-colors hover:bg-3b-background-medium',
                )}
                tabIndex={0}
              >
                {label(_page)}
              </TrackedAnchor>
            )}
          </li>
        );
      })}
    </ol>
  );
}
